import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Package, Plus, Search, Filter, RefreshCw, Edit, Eye, X,
  Wrench, Settings, AlertTriangle, ChevronRight, Truck, Box,
  DollarSign, Calendar, Hash, Tag } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI } from '../hooks/useFleetData';

const API_URL = API_BASE;

// Category icons and colors
const categoryConfig = {
  engine: { icon: '⚙️', color: 'bg-red-100 text-red-700' },
  transmission: { icon: '🔧', color: 'bg-orange-100 text-orange-700' },
  brakes: { icon: '🛑', color: 'bg-yellow-100 text-yellow-700' },
  suspension: { icon: '🔩', color: 'bg-green-100 text-green-700' },
  electrical: { icon: '⚡', color: 'bg-blue-100 text-blue-700' },
  tires: { icon: '🛞', color: 'bg-gray-100 text-gray-700' },
  filters: { icon: '🌀', color: 'bg-purple-100 text-purple-700' },
  fluids: { icon: '💧', color: 'bg-cyan-100 text-cyan-700' },
  body: { icon: '🚗', color: 'bg-indigo-100 text-indigo-700' },
  lighting: { icon: '💡', color: 'bg-amber-100 text-amber-700' },
  cooling: { icon: '❄️', color: 'bg-sky-100 text-sky-700' },
  fuel_system: { icon: '⛽', color: 'bg-emerald-100 text-emerald-700' },
  other: { icon: '📦', color: 'bg-slate-100 text-slate-700' }
};

const conditionConfig = {
  new: { label: 'New', color: 'bg-emerald-500' },
  used: { label: 'Used', color: 'bg-amber-500' },
  refurbished: { label: 'Refurbished', color: 'bg-blue-500' },
  damaged: { label: 'Damaged', color: 'bg-red-500' },
  expired: { label: 'Expired', color: 'bg-gray-500' }
};

const PartsManagementPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('catalog');
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inventorySummary, setInventorySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load categories
      const cats = await fetchAPI('/parts/categories');
      setCategories(cats);

      // Load parts
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      const partsData = await fetchAPI(`/parts?${params.toString()}`);
      setParts(partsData);

      // Load inventory summary
      const invSummary = await fetchAPI('/inventory/summary');
      setInventorySummary(invSummary);
    } catch (err) {
      console.error('Failed to load parts data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredParts = parts.filter((p) =>
  p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  p.part_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  p.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalParts = inventorySummary.reduce((sum, cat) => sum + cat.total_parts, 0);
  const totalQuantity = inventorySummary.reduce((sum, cat) => sum + cat.total_quantity, 0);
  const totalValue = inventorySummary.reduce((sum, cat) => sum + cat.total_value, 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("partsManagement", "Parts Management")}</h1>
          <p className="text-gray-500">{t("manageVehicleParts", "Manage vehicle parts catalog and inventory")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowAddModal(true)}>
            
            <Plus className="w-4 h-4 mr-2" />{t("addPart", "Add Part")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">{t("totalParts", "Total Parts")}</p>
                <p className="text-2xl font-bold text-gray-900">{totalParts}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">{t("inStock", "In Stock")}</p>
                <p className="text-2xl font-bold text-gray-900">{totalQuantity.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Box className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">{t("inventoryValue", "Inventory Value")}</p>
                <p className="text-2xl font-bold text-gray-900">{t("r", "R$")}
                  {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">{t("categories", "Categories")}</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="catalog" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">{t("partsCatalog", "Parts Catalog")}

          </TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">{t("inventory", "Inventory")}

          </TabsTrigger>
          <TabsTrigger value="installed" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">{t("installedParts", "Installed Parts")}

          </TabsTrigger>
        </TabsList>

        {/* Parts Catalog Tab */}
        <TabsContent value="catalog" className="space-y-4">
          {/* Search and Filters */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search parts by name, number, manufacturer..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} />
                  
                </div>
                <select
                  className="border rounded-lg p-2 text-sm min-w-[200px]"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}>
                  
                  <option value="">{t("allCategories", "All Categories")}</option>
                  {categories.map((cat) =>
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                  )}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Parts Grid */}
          {loading ?
          <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
            </div> :
          filteredParts.length === 0 ?
          <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noPartsFound", "No parts found")}</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || selectedCategory ?
                'Try adjusting your search or filters' :
                'Get started by adding parts to the catalog'
                }
                </p>
                <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => setShowAddModal(true)}>
                
                  <Plus className="w-4 h-4 mr-2" />{t("addPart", "Add Part")}

              </Button>
              </CardContent>
            </Card> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredParts.map((part) =>
            <PartCard
              key={part.id}
              part={part}
              onClick={() => setSelectedPart(part)} />

            )}
            </div>
          }
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-4">
              <CardTitle className="text-lg">{t("inventoryByCategory", "Inventory by Category")}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {inventorySummary.length === 0 ?
                <div className="p-8 text-center">
                    <Box className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t("noInventoryData", "No inventory data available")}</p>
                  </div> :

                inventorySummary.map((cat) => {
                  const config = categoryConfig[cat.category] || categoryConfig.other;
                  return (
                    <div key={cat.category} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${config.color}`}>
                            {config.icon}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 capitalize">
                              {cat.category.replace('_', ' ')}
                            </p>
                            <p className="text-sm text-gray-500">{cat.total_parts}{t("parts", "parts")}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{cat.total_quantity.toLocaleString()}{t("units", "units")}</p>
                          <p className="text-sm text-gray-500">{t("r", "R$")}
                          {cat.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>);

                })
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Installed Parts Tab */}
        <TabsContent value="installed" className="space-y-4">
          <InstalledPartsSection />
        </TabsContent>
      </Tabs>

      {/* Add Part Modal */}
      {showAddModal &&
      <AddPartModal
        categories={categories}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadData();
        }} />

      }

      {/* Part Detail Modal */}
      {selectedPart &&
      <PartDetailModal
        part={selectedPart}
        onClose={() => setSelectedPart(null)} />

      }
    </div>);

};

// Part Card Component
const PartCard = ({ part, onClick }) => {const { t } = useTranslation("fleet");
  const config = categoryConfig[part.category] || categoryConfig.other;

  return (
    <Card
      className="bg-white border-0 shadow-sm hover:shadow-lg transition-all cursor-pointer"
      onClick={onClick}>
      
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${config.color}`}>
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 truncate">{part.name}</p>
            <p className="text-sm text-gray-500">{part.part_number}</p>
          </div>
        </div>
        
        <div className="mt-4 space-y-2 text-sm">
          {part.manufacturer &&
          <div className="flex items-center justify-between">
              <span className="text-gray-500">{t("manufacturer", "Manufacturer")}</span>
              <span className="font-medium">{part.manufacturer}</span>
            </div>
          }
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t("category", "Category")}</span>
            <Badge variant="outline" className="capitalize text-xs">
              {part.category?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t("price", "Price")}</span>
            <span className="font-bold text-gray-900">{t("r", "R$")}
              {(part.unit_price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>);

};

// Installed Parts Section
const InstalledPartsSection = () => {const { t } = useTranslation("fleet");
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleParts, setVehicleParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const data = await fetchAPI('/vehicles?limit=100');
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const loadVehicleParts = async (vehicleId) => {
    setLoading(true);
    try {
      const data = await fetchAPI(`/vehicles/${vehicleId}/parts`);
      setVehicleParts(data);
    } catch (err) {
      console.error('Failed to load vehicle parts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    loadVehicleParts(vehicle.id);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Vehicle List */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="border-b py-3">
          <CardTitle className="text-base">{t("selectVehicle", "Select Vehicle")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 max-h-96 overflow-auto">
          <div className="divide-y">
            {vehicles.length === 0 ?
            <div className="p-6 text-center">
                <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{t("noVehiclesFound", "No vehicles found")}</p>
              </div> :

            vehicles.map((vehicle) =>
            <div
              key={vehicle.id}
              className={`p-3 cursor-pointer hover:bg-gray-50 ${
              selectedVehicle?.id === vehicle.id ? 'bg-red-50 border-l-4 border-red-500' : ''}`
              }
              onClick={() => handleVehicleSelect(vehicle)}>
              
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{vehicle.vehicle_code}</p>
                      <p className="text-xs text-gray-500">{vehicle.license_plate}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
            )
            }
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Parts */}
      <div className="lg:col-span-2">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="border-b py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {selectedVehicle ?
                `Parts on ${selectedVehicle.vehicle_code}` :
                'Vehicle Parts'
                }
              </CardTitle>
              {selectedVehicle &&
              <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" />{t("installPart", "Install Part")}

              </Button>
              }
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!selectedVehicle ?
            <div className="p-12 text-center">
                <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t("selectAVehicle", "Select a vehicle to view installed parts")}</p>
              </div> :
            loading ?
            <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin text-red-500 mx-auto" />
              </div> :
            vehicleParts.length === 0 ?
            <div className="p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t("noPartsRecorded", "No parts recorded for this vehicle")}</p>
                <Button variant="outline" size="sm" className="mt-4">
                  <Plus className="w-4 h-4 mr-1" />{t("installFirstPart", "Install First Part")}

              </Button>
              </div> :

            <div className="divide-y max-h-96 overflow-auto">
                {vehicleParts.map((vp) => {
                const condConfig = conditionConfig[vp.condition] || conditionConfig.new;
                return (
                  <div key={vp.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{vp.part?.name}</p>
                          <p className="text-sm text-gray-500">{vp.part?.part_number}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`${condConfig.color} text-white text-xs`}>
                              {condConfig.label}
                            </Badge>
                            <span className="text-xs text-gray-400">{t("health", "Health:")}
                            {vp.current_health_percent?.toFixed(0) || 100}%
                            </span>
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-gray-500">{t("installed", "Installed")}</p>
                          <p className="font-medium">
                            {vp.installation_date ?
                          new Date(vp.installation_date).toLocaleDateString() :
                          '-'
                          }
                          </p>
                          {vp.installation_odometer_km &&
                        <p className="text-xs text-gray-400">
                              @ {vp.installation_odometer_km.toLocaleString()}{t("km", "km")}
                        </p>
                        }
                        </div>
                      </div>
                    </div>);

              })}
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};

// Add Part Modal
const AddPartModal = ({ categories, onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    name: '',
    category: 'other',
    manufacturer: '',
    manufacturer_part_number: '',
    brand: '',
    unit_price: '',
    warranty_months: '',
    typical_lifespan_km: '',
    universal: false
  });
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchAPI('/parts', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          unit_price: formData.unit_price ? parseFloat(formData.unit_price) : 0,
          warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
          typical_lifespan_km: formData.typical_lifespan_km ? parseFloat(formData.typical_lifespan_km) : null
        })
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create part:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <CardTitle>{t("addNewPart", "Add New Part")}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("partName", "Part Name *")}</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Oil Filter"
                required />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("category", "Category *")}</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={formData.category}
                  onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))}
                  required>
                  
                  {categories.map((cat) =>
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("manufacturer", "Manufacturer")}</label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => setFormData((f) => ({ ...f, manufacturer: e.target.value }))}
                  placeholder="e.g., Bosch" />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("manufacturerPart", "Manufacturer Part #")}</label>
                <Input
                  value={formData.manufacturer_part_number}
                  onChange={(e) => setFormData((f) => ({ ...f, manufacturer_part_number: e.target.value }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("brand", "Brand")}</label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData((f) => ({ ...f, brand: e.target.value }))} />
                
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("unitPriceR", "Unit Price (R$)")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("warrantyMonths", "Warranty (months)")}</label>
                <Input
                  type="number"
                  value={formData.warranty_months}
                  onChange={(e) => setFormData((f) => ({ ...f, warranty_months: e.target.value }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("lifespanKm", "Lifespan (km)")}</label>
                <Input
                  type="number"
                  value={formData.typical_lifespan_km}
                  onChange={(e) => setFormData((f) => ({ ...f, typical_lifespan_km: e.target.value }))} />
                
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="universal"
                checked={formData.universal}
                onChange={(e) => setFormData((f) => ({ ...f, universal: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-red-500" />
              
              <label htmlFor="universal" className="text-sm text-gray-700">{t("universalPartFits", "Universal part (fits any vehicle)")}

              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}>
                
                {loading ?
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> :

                <Plus className="w-4 h-4 mr-2" />
                }{t("addPart", "Add Part")}

              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

// Part Detail Modal
const PartDetailModal = ({ part, onClose }) => {const { t } = useTranslation("fleet");
  const config = categoryConfig[part.category] || categoryConfig.other;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-lg m-4 bg-white" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${config.color}`}>
                {config.icon}
              </div>
              <div>
                <CardTitle>{part.name}</CardTitle>
                <p className="text-sm text-gray-500">{part.part_number}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t("category", "Category")}</p>
              <p className="font-medium capitalize">{part.category?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("manufacturer", "Manufacturer")}</p>
              <p className="font-medium">{part.manufacturer || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("brand", "Brand")}</p>
              <p className="font-medium">{part.brand || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("unitPrice", "Unit Price")}</p>
              <p className="font-bold text-lg">{t("r", "R$")}
                {(part.unit_price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {(part.warranty_months || part.typical_lifespan_km) &&
          <div className="pt-4 border-t grid grid-cols-2 gap-4">
              {part.warranty_months &&
            <div>
                  <p className="text-sm text-gray-500">{t("warranty", "Warranty")}</p>
                  <p className="font-medium">{part.warranty_months}{t("months", "months")}</p>
                </div>
            }
              {part.typical_lifespan_km &&
            <div>
                  <p className="text-sm text-gray-500">{t("typicalLifespan", "Typical Lifespan")}</p>
                  <p className="font-medium">{part.typical_lifespan_km.toLocaleString()}{t("km", "km")}</p>
                </div>
            }
            </div>
          }

          <div className="pt-4 border-t flex justify-end gap-2">
            <Button variant="outline">
              <Edit className="w-4 h-4 mr-2" />{t("edit", "Edit")}

            </Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white">
              <Truck className="w-4 h-4 mr-2" />{t("installOnVehicle", "Install on Vehicle")}

            </Button>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default PartsManagementPage;